import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);
dayjs.extend(utc);
dayjs.extend(timezone);

const WeightPerDayChart = ({ weightPerDay }) => {
  const theme = useTheme();
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (weightPerDay) {
      const dayLabels = weightPerDay.map((weightPerDayObj) =>
        dayjs(weightPerDayObj["date"]).format("ddd")
      );

      const dayWeights = weightPerDay.map(
        (weightPerDayObj) => weightPerDayObj.weight
      );

      const daySets = weightPerDay.map(
        (weightPerDayObj) => weightPerDayObj.sets
      );

      const dayReps = weightPerDay.map(
        (weightPerDayObj) => weightPerDayObj.reps
      );

      const data = {
        labels: dayLabels,
        datasets: [
          {
            label: "Weight",
            data: dayWeights,
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main,
            fill: false,
            tension: 0.1,
            yAxisID: "y-axis-weight",
          },
          {
            label: "Sets",
            data: daySets,
            borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.main,
            fill: false,
            tension: 0.1,
            yAxisID: "y-axis-sets-reps",
          },
          {
            label: "Reps",
            data: dayReps,
            borderColor: theme.palette.tertiary.main,
            backgroundColor: theme.palette.tertiary.main,
            fill: false,
            tension: 0.1,
            yAxisID: "y-axis-sets-reps",
          },
        ],
      };

      setChartData(data);
    }
  }, [weightPerDay, theme]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          color: theme.palette.text.primary,
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.primary,
          font: {
            size: 12,
          },
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      "y-axis-weight": {
        type: "linear",
        position: "left",
        ticks: {
          color: theme.palette.text.primary,
          font: {
            size: 12,
          },
        },
        grid: {
          color: theme.palette.divider,
        },
      },
      "y-axis-sets-reps": {
        type: "linear",
        position: "right",
        ticks: {
          color: theme.palette.text.primary,
          font: {
            size: 12,
          },
        },
        grid: {
          drawOnChartArea: false, // Prevent overlapping grids
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", height: "164px", mt: 2 }}>
      {chartData ? (
        <Line options={options} data={chartData} />
      ) : (
        <Typography>No data available</Typography>
      )}
    </Box>
  );
};

export default WeightPerDayChart;
